import React from 'react';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Box, 
  Stack,
  Grid
} from '@mui/material';
import { 
  People as Users, 
  CalendarMonth as Calendar, 
  Person as User, 
  ConfirmationNumber as Ticket, 
  Link as LinkIcon, 
  ContentCopy as Copy 
} from '@mui/icons-material';
import { Trip } from '@/types';
import { calculateTripDuration, getSkiPassBookingStats } from '@/utils/calculations';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import StatsCard from '@/components/ui/StatsCard';
import { useI18n } from '@/hooks/useI18n';

interface TripOverviewStatsProps {
  trip: Trip;
}

const TripOverviewStats: React.FC<TripOverviewStatsProps> = ({ trip }) => {
  const duration = calculateTripDuration(trip);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, formatDate } = useI18n();

  const generateSignUpUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/trip/${trip.id}/signup`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast({
          title: t('trip.pages.overview.copiedToClipboard'),
          description: t('trip.pages.overview.signUpLinkCopied'),
        });
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        toast({
          title: t('common.error'),
          description: t('trip.pages.overview.couldNotCopyLink'),
          variant: "destructive",
        });
      });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatsCard
            emoji="👥"
            title={t('trip.pages.overview.totalTravellers')}
            value={trip.statistics.travellers.total}
            subtitle={t('trip.pages.overview.skiClubMembers', { count: trip.statistics.travellers.totalSkiClubMembers })}
            icon={Users}
            iconColor="primary.main"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatsCard
            emoji="📅"
            title={t('trip.pages.overview.tripDuration')}
            value={t('trip.pages.overview.durationDays', { count: duration })}
            subtitle={`${formatDate(trip.startDate)} - ${formatDate(trip.endDate)}`}
            icon={Calendar}
            iconColor="success.main"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatsCard
            emoji="🏠"
            title={t('trip.pages.overview.apartments')}
            value={trip.statistics.apartments.total}
            subtitle={t('trip.pages.overview.bedsOccupied', { 
              occupied: trip.statistics.apartments.totalOccupancy, 
              total: trip.statistics.apartments.totalNumberOfBeds 
            })}
            icon={User}
            iconColor="secondary.main"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatsCard
            emoji="🎿"
            title={t('trip.pages.overview.skiPassesBooked')}
            value={trip.statistics.skiPasses.total}
            subtitle={t('trip.pages.overview.totalBookedPasses')}
            icon={Ticket}
            iconColor="warning.main"
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              🔗 {t('trip.pages.overview.tripSignUpLink')}
            </Typography>
            <LinkIcon sx={{ fontSize: 20, color: 'info.main' }} />
          </Box>
          
          <Typography variant="caption" sx={{ 
            color: 'text.secondary', 
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 2,
            p: 1,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: 1,
            borderColor: 'grey.200'
          }}>
            {generateSignUpUrl()}
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => copyToClipboard(generateSignUpUrl())}
              startIcon={<Copy sx={{ fontSize: 14 }} />}
              sx={{ flexGrow: 1 }}
            >
              {t('trip.pages.overview.copyLink')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/trip/${trip.id}/signup`)}
              startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
              sx={{ flexGrow: 1 }}
            >
              {t('trip.pages.overview.goToSignUpPage')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TripOverviewStats;
